import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { BuiltForShopifyBadge, WoocommerceLogo } from "../../assets/assets";
import { sendSlackMessage } from "../../models/notifications";

// ─── tokens ───────────────────────────────────────────────────────────────────

const T = {
  bg:           "linear-gradient(180deg, #f7f5f2 0%, #ffffff 60%)",
  accent:       "#16a34a",
  accentDim:    "rgba(22,163,74,0.08)",
  accentBorder: "rgba(22,163,74,0.25)",
  textDark:     "#0f0f10",
  textMuted:    "#6b7280",
  textFaint:    "#9ca3af",
  border:       "rgba(0,0,0,0.07)",
  font:         "'DM Sans', system-ui, sans-serif",
};

function TrialBadge() {
  return (
    <div
      style={{
        display:      "inline-flex",
        alignItems:   "center",
        gap:          "7px",
        background:   "#ffffff",
        border:       "1px solid rgba(0,0,0,0.11)",
        borderRadius: "99px",
        padding:      "5px 13px 5px 9px",
      }}
    >
      {/* Calendar icon with "14" inside */}
      <svg width="17" height="17" viewBox="0 0 17 17" fill="none" xmlns="http://www.w3.org/2000/svg">
        <rect x="1" y="2.5" width="15" height="13.5" rx="2" stroke="#16a34a" strokeWidth="1.3"/>
        <path d="M1 7h15" stroke="#16a34a" strokeWidth="1.3" strokeLinecap="round"/>
        <path d="M5 1v3M12 1v3" stroke="#16a34a" strokeWidth="1.3" strokeLinecap="round"/>
        <text
          x="8.5" y="13.5"
          textAnchor="middle"
          fontSize="5.5"
          fontWeight="800"
          fill="#16a34a"
          fontFamily="system-ui, sans-serif"
        >
          14
        </text>
      </svg>
      <span
        style={{
          fontSize:      "0.75rem",
          fontWeight:    600,
          color:         "#1a1a1a",
          fontFamily:    T.font,
          letterSpacing: "0.01em",
          whiteSpace:    "nowrap",
        }}
      >
        14-day free trial
      </span>
    </div>
  );
}

const STATS = [
  {
    value: "13×",
    label: "average ROI",
    info:  "Merchants using Pabloo's store credit and cashback programs see an average 13× return on investment versus traditional discount strategies.",
  },
  {
    value: "$45M+",
    label: "store credit managed",
    info:  "Over $45 million in store credit has been issued and managed across all Pabloo merchants to date.",
  },
  {
    value: "$21K",
    label: "average annual savings",
    info:  "Merchants save an average of $21,000 per year by eliminating payment processing fees on refunds and retaining revenue that would otherwise leave the business.",
  },
];

function InfoTooltip({ text }) {
  const [visible, setVisible] = useState(false);
  return (
    <span
      style={{ position: "relative", display: "inline-flex", alignItems: "center", marginLeft: "5px", verticalAlign: "middle" }}
      onMouseEnter={() => setVisible(true)}
      onMouseLeave={() => setVisible(false)}
    >
      <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ cursor: "default", flexShrink: 0 }}>
        <circle cx="7" cy="7" r="6.5" stroke={T.textFaint} strokeWidth="1"/>
        <text x="7" y="11" textAnchor="middle" fontSize="8" fontWeight="700" fill={T.textFaint} fontFamily="system-ui, sans-serif">i</text>
      </svg>
      {visible && (
        <span style={{
          position:      "absolute",
          bottom:        "calc(100% + 8px)",
          left:          "50%",
          transform:     "translateX(-50%)",
          background:    "#1a1a1a",
          color:         "#ffffff",
          fontSize:      "0.75rem",
          fontFamily:    T.font,
          lineHeight:    1.5,
          fontWeight:    400,
          padding:       "8px 12px",
          borderRadius:  "8px",
          width:         "220px",
          textAlign:     "left",
          zIndex:        50,
          pointerEvents: "none",
          boxShadow:     "0 4px 16px rgba(0,0,0,0.18)",
          whiteSpace:    "normal",
        }}>
          {text}
          <span style={{
            position:    "absolute",
            top:         "100%",
            left:        "50%",
            transform:   "translateX(-50%)",
            width:       0,
            height:      0,
            borderLeft:  "5px solid transparent",
            borderRight: "5px solid transparent",
            borderTop:   "5px solid #1a1a1a",
          }} />
        </span>
      )}
    </span>
  );
}

// ─── PablooHeroComponent ──────────────────────────────────────────────────────

export default function PablooHeroComponent() {
  const navigate = useNavigate();

  const handleStartFree = () => {
    sendSlackMessage("Hero CTA: Start for free clicked.", "#website-updates");
    window.gtag?.("event", "conversion_button_clicked", {
      button: "view_shopify_listing",
      source: "Hero section",
    });
    window.location.href = "https://app.pabloo.com/sign-up";
  };

  const handleBookCall = () => {
    sendSlackMessage("Hero CTA: Book a call clicked.", "#website-updates");
    navigate("/bookCall");
  };

  return (
    <section
      style={{
        background: T.bg,
        padding:    "72px 24px 80px",
        textAlign:  "center",
      }}
    >
      <div style={{ maxWidth: "800px", margin: "0 auto" }}>

        {/* Eyebrow */}
        <div
          style={{
            display:       "inline-flex",
            alignItems:    "center",
            gap:           "7px",
            background:    T.accentDim,
            border:        `1px solid ${T.accentBorder}`,
            borderRadius:  "99px",
            padding:       "5px 14px",
            marginBottom:  "32px",
          }}
        >
          <span
            style={{
              width: "6px", height: "6px",
              borderRadius: "50%",
              background: T.accent,
              flexShrink: 0,
            }}
          />
          <span
            style={{
              fontSize:      "0.8125rem",
              fontWeight:    600,
              color:         T.accent,
              fontFamily:    T.font,
              letterSpacing: "0.01em",
            }}
          >
            Customer economy for Shopify brands
          </span>
        </div>

        {/* Headline */}
        <h1
          style={{
            fontFamily:    T.font,
            fontWeight:    800,
            lineHeight:    1.1,
            letterSpacing: "-0.03em",
            marginBottom:  "28px",
          }}
        >
          <span
            style={{
              display:   "block",
              fontSize:  "clamp(2.25rem, 5vw, 3.75rem)",
              color:     T.textMuted,
              fontWeight:600,
            }}
          >
            Most brands discount.
          </span>
          <span
            style={{
              display:  "block",
              fontSize: "clamp(2.25rem, 5vw, 3.75rem)",
              color:    T.textDark,
            }}
          >
            The best build economies.
          </span>
        </h1>

        {/* Subheadline */}
        <p
          style={{
            fontFamily:   T.font,
            fontSize:     "1.125rem",
            color:        T.textMuted,
            lineHeight:   1.7,
            maxWidth:     "640px",
            margin:       "0 auto 40px",
          }}
        >
          Pabloo is the credit ledger serious Shopify brands use to convert
          refunds into retained revenue, run cashback campaigns that compound,
          and build the kind of loyalty that doesn't require a sale.
        </p>

        {/* CTAs */}
        <div
          className="flex flex-col sm:flex-row items-center justify-center gap-3"
          style={{ marginBottom: "48px" }}
        >
          <button
            type="button"
            onClick={handleStartFree}
            style={{
              display:       "inline-flex",
              alignItems:    "center",
              gap:           "8px",
              padding:       "14px 32px",
              borderRadius:  "99px",
              background:    T.accent,
              color:         "#ffffff",
              fontSize:      "0.9375rem",
              fontWeight:    700,
              border:        "none",
              cursor:        "pointer",
              fontFamily:    T.font,
              transition:    "background 0.15s",
              whiteSpace:    "nowrap",
            }}
            onMouseEnter={(e) => { e.currentTarget.style.background = "#15803d"; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = T.accent; }}
          >
            Start for free
            <svg width="8" height="13" viewBox="0 0 8 13" fill="none">
              <path d="M1.5 1.5L6.5 6.5L1.5 11.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>

          <button
            type="button"
            onClick={handleBookCall}
            style={{
              display:       "inline-flex",
              alignItems:    "center",
              justifyContent:"center",
              padding:       "14px 32px",
              borderRadius:  "99px",
              background:    "transparent",
              color:         T.textMuted,
              fontSize:      "0.9375rem",
              fontWeight:    600,
              border:        `1px solid ${T.border}`,
              cursor:        "pointer",
              fontFamily:    T.font,
              transition:    "border-color 0.15s, color 0.15s",
              whiteSpace:    "nowrap",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = "rgba(0,0,0,0.18)";
              e.currentTarget.style.color = T.textDark;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = T.border;
              e.currentTarget.style.color = T.textMuted;
            }}
          >
            Book a call
          </button>
        </div>

        {/* Stats */}
        <div
          className="flex flex-col sm:flex-row items-center justify-center gap-6 sm:gap-10"
          style={{ marginBottom: "56px" }}
        >
          {STATS.map((s, i) => (
            <React.Fragment key={s.value}>
              {i > 0 && (
                <div
                  className="hidden sm:block"
                  style={{ width: "1px", height: "28px", background: T.border }}
                />
              )}
              <div style={{ textAlign: "center" }}>
                <div
                  style={{
                    fontSize:      "1.5rem",
                    fontWeight:    800,
                    color:         T.textDark,
                    fontFamily:    T.font,
                    letterSpacing: "-0.02em",
                    lineHeight:    1,
                    marginBottom:  "3px",
                    display:       "inline-flex",
                    alignItems:    "center",
                  }}
                >
                  {s.value}
                  <InfoTooltip text={s.info} />
                </div>
                <div
                  style={{
                    fontSize:   "0.8125rem",
                    color:      T.textFaint,
                    fontFamily: T.font,
                  }}
                >
                  {s.label}
                </div>
              </div>
            </React.Fragment>
          ))}
        </div>

        {/* Trust badges */}
        <div
          style={{
            display:        "flex",
            alignItems:     "center",
            justifyContent: "center",
            gap:            "32px",
            flexWrap:       "wrap",
            opacity:        0.65,
          }}
        >
          <img src={WoocommerceLogo}      alt="WooCommerce"      style={{ height: "22px", width: "auto" }} loading="lazy" />
          <img src={BuiltForShopifyBadge} alt="Built for Shopify" style={{ height: "28px", width: "auto" }} loading="lazy" />
          <TrialBadge />
        </div>

      </div>
    </section>
  );
}
